import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/lib/utils';

/**
 * StatusBadge - Consistent status indicators across the application
 *
 * Features a colored dot indicator with matching text and subtle background.
 * Use for inventory thresholds, task states, health indicators, etc.
 */

const statusBadgeVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border-none px-2 py-0.5 text-xs font-medium',
  {
    variants: {
      status: {
        warning: 'bg-warning/10 text-warning-text',
        error: 'bg-destructive/10 text-destructive',
        success: 'bg-success/10 text-success-text',
        info: 'bg-info/10 text-info',
        limit: 'bg-info/10 text-info',
        neutral: 'bg-muted text-muted-foreground',
      },
      size: {
        sm: 'px-1.5 py-0.5 text-[10px]',
        md: 'px-2 py-0.5 text-xs',
        lg: 'px-2.5 py-1 text-sm',
      },
    },
    defaultVariants: {
      status: 'neutral',
      size: 'md',
    },
  }
);

const dotColors: Record<string, string> = {
  warning: 'bg-warning',
  error: 'bg-destructive',
  success: 'bg-success',
  info: 'bg-info',
  limit: 'bg-info',
  neutral: 'bg-muted-foreground',
};

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof statusBadgeVariants> {
  /** Show the colored dot indicator (default: true) */
  showDot?: boolean;
  /** Optional icon to show instead of/alongside the dot */
  icon?: React.ReactNode;
}

function StatusBadge({
  className,
  status,
  size,
  showDot = true,
  icon,
  children,
  ...props
}: StatusBadgeProps) {
  const dotSize = size === 'sm' ? 'size-1' : size === 'lg' ? 'size-2' : 'size-1.5';

  return (
    <span className={cn(statusBadgeVariants({ status, size }), className)} {...props}>
      {icon}
      {showDot && !icon && (
        <span
          className={cn('rounded-full', dotSize, dotColors[status ?? 'neutral'])}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}

export { StatusBadge, statusBadgeVariants };
